import React, { useState, useEffect } from 'react';
import {
  CommunityJuryEngine,
  DisputeIncident,
  JurorVoteChoice,
} from '../../../domain/moderation/community-jury-engine';

export interface JuryChamberModalProps {
  disputeId?: string;
  currentJurorDid?: string;
  isOpen?: boolean;
  onClose: () => void;
  onVerdictSettled?: () => void;
}

export const JuryChamberModal: React.FC<JuryChamberModalProps> = ({
  disputeId: propDisputeId,
  currentJurorDid = 'did:key:juror_node_1',
  isOpen = true,
  onClose,
  onVerdictSettled,
}) => {
  const [incident, setIncident] = useState<DisputeIncident | undefined>(undefined);
  const [selectedChoice, setSelectedChoice] = useState<JurorVoteChoice>('GUILTY');
  const [secretSalt, setSecretSalt] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // If disputeId is not provided via props, find or create default incident for demo
  const disputeId = propDisputeId || incident?.disputeId || 'disp_live_tribunal';

  useEffect(() => {
    if (!isOpen) return;

    let inc = CommunityJuryEngine.getIncident(disputeId);
    if (!inc) {
      // Seed an active demo incident for the jury modal
      inc = CommunityJuryEngine.fileDispute({
        disputeId,
        reporterDid: 'did:key:reporter_alpha',
        defendantDid: 'did:key:scammer_bravo',
        category: 'PHISHING_OR_MALICIOUS_LINK',
        evidenceUri: 'https://proofs.herx.org/evidence/demo_phish',
        evidencePayload: 'Malicious firmware bundle distributing infostealers',
        blockRootHash: '0x889a712f8e9102ab3c49817290123e4',
        eligiblePool: Array.from({ length: 15 }).map((_, i) => ({
          did: `did:key:juror_node_${i + 1}`,
          sovereignScore: 650 + i * 15,
          isSlashed: false,
          vouchedForDids: [],
          vouchedByDids: [],
        })),
      });
    }
    setIncident(inc);

    // Load persisted salt for this juror and dispute from localStorage if available
    const cachedSalt = localStorage.getItem(`jury_salt_${disputeId}_${currentJurorDid}`);
    if (cachedSalt) {
      setSecretSalt(cachedSalt);
    } else {
      const generated = Math.random().toString(36).substring(2) + Date.now().toString(36);
      setSecretSalt(generated);
    }
  }, [isOpen, disputeId, currentJurorDid]);

  if (!isOpen) return null;

  if (!incident) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0,0,0,0.8)',
          backdropFilter: 'blur(8px)',
          padding: 16,
        }}
      >
        <div
          style={{
            background: '#111318',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 16,
            padding: 24,
            color: '#ffffff',
            maxWidth: 400,
            textAlign: 'center',
          }}
        >
          <p style={{ color: '#f87171', fontWeight: 600, marginBottom: 16 }}>Dispute Not Found</p>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 16px',
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: 8,
              color: '#ffffff',
              cursor: 'pointer',
            }}
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  const isSummoned = incident.selectedJurorDids.includes(currentJurorDid);
  const hasCommitted = !!incident.commitments[currentJurorDid];
  const hasRevealed = !!incident.reveals[currentJurorDid];

  const handleCommit = () => {
    try {
      setIsSubmitting(true);
      const commitment = CommunityJuryEngine.generateCommitment(selectedChoice, secretSalt, disputeId);
      CommunityJuryEngine.submitCommitment(disputeId, currentJurorDid, commitment);
      localStorage.setItem(`jury_salt_${disputeId}_${currentJurorDid}`, secretSalt);
      localStorage.setItem(`jury_choice_${disputeId}_${currentJurorDid}`, selectedChoice);
      setIncident({ ...CommunityJuryEngine.getIncident(disputeId)! });
      setStatusMsg('Ballot encrypted & committed successfully.');
    } catch (err: any) {
      setStatusMsg(`Error: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReveal = () => {
    try {
      setIsSubmitting(true);
      const savedChoice =
        (localStorage.getItem(`jury_choice_${disputeId}_${currentJurorDid}`) as JurorVoteChoice) ||
        selectedChoice;
      CommunityJuryEngine.submitReveal(disputeId, currentJurorDid, savedChoice, secretSalt);
      const updated = CommunityJuryEngine.getIncident(disputeId)!;
      setIncident({ ...updated });
      setStatusMsg('Vote preimage verified and recorded on ledger.');
      if (updated.phase === 'SETTLED' && onVerdictSettled) {
        onVerdictSettled();
      }
    } catch (err: any) {
      setStatusMsg(`Verification Failed: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.85)',
        backdropFilter: 'blur(12px)',
        padding: 16,
      }}
    >
      <div
        style={{
          background: '#0e1017',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 24,
          maxWidth: 640,
          width: '100%',
          padding: 24,
          color: '#ffffff',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
          position: 'relative',
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            paddingBottom: 16,
            marginBottom: 20,
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  fontSize: 11,
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: 'rgba(99, 102, 241, 0.2)',
                  color: '#818cf8',
                  border: '1px solid rgba(99, 102, 241, 0.3)',
                  fontFamily: 'monospace',
                }}
              >
                JURY SUMMONS #{incident.disputeId.substring(0, 8)}
              </span>
              <span
                style={{
                  fontSize: 11,
                  padding: '2px 8px',
                  borderRadius: 4,
                  background: 'rgba(255,255,255,0.05)',
                  color: '#9ca3af',
                }}
              >
                Phase: <strong style={{ color: '#ffffff' }}>{incident.phase}</strong>
              </span>
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 700, margin: '6px 0 0', letterSpacing: 0.2 }}>
              Peer Anti-Scam Tribunal
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#9ca3af',
              fontSize: 18,
              cursor: 'pointer',
              padding: 6,
            }}
          >
            ✕
          </button>
        </div>

        {/* Evidence & Case Dossier */}
        <div
          style={{
            background: '#151822',
            border: '1px solid rgba(255,255,255,0.05)',
            borderRadius: 16,
            padding: 16,
            marginBottom: 20,
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 12,
              fontSize: 11,
              fontFamily: 'monospace',
              color: '#9ca3af',
              marginBottom: 12,
            }}
          >
            <div>
              <span style={{ color: '#6b7280' }}>DEFENDANT:</span>{' '}
              <span style={{ color: '#ffffff' }}>{incident.defendantDid.substring(0, 16)}...</span>
            </div>
            <div>
              <span style={{ color: '#6b7280' }}>CATEGORY:</span>{' '}
              <span style={{ color: '#fbbf24', fontWeight: 600 }}>{incident.category}</span>
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <span style={{ color: '#6b7280' }}>EVIDENCE HASH:</span>{' '}
              <span style={{ color: '#34d399' }}>{incident.evidencePayloadHash}</span>
            </div>
          </div>
          <div
            style={{
              fontSize: 12,
              color: '#d1d5db',
              background: 'rgba(0,0,0,0.4)',
              padding: 12,
              borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.05)',
            }}
          >
            <p style={{ margin: 0 }}>
              <strong>Evidence Narrative:</strong> {incident.evidenceUri}
            </p>
          </div>
        </div>

        {/* Status Indicators */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 12,
            color: '#9ca3af',
            border: '1px solid rgba(255,255,255,0.05)',
            borderRadius: 12,
            padding: 12,
            background: 'rgba(255,255,255,0.02)',
            marginBottom: 20,
          }}
        >
          <div>
            Commitments:{' '}
            <strong style={{ color: '#818cf8' }}>
              {Object.keys(incident.commitments).length}/7
            </strong>
          </div>
          <div>
            Reveals:{' '}
            <strong style={{ color: '#34d399' }}>
              {Object.keys(incident.reveals).length}/7
            </strong>
          </div>
          <div>
            Quorum Required:{' '}
            <strong style={{ color: '#fbbf24' }}>5 of 7 Supermajority</strong>
          </div>
        </div>

        {/* Action Panel */}
        {!isSummoned ? (
          <div style={{ textAlign: 'center', padding: '24px 0', fontSize: 13, color: '#9ca3af' }}>
            You are an observer. Only the 7 deterministically drafted Tier 4 jurors may vote on this docket.
          </div>
        ) : incident.phase === 'SETTLED' ? (
          <div
            style={{
              textAlign: 'center',
              padding: 16,
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: 16,
            }}
          >
            <p
              style={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: 1,
                color: '#34d399',
                fontFamily: 'monospace',
                margin: 0,
              }}
            >
              Final Quorum Verdict
            </p>
            <p style={{ fontSize: 24, fontWeight: 900, color: '#ffffff', margin: '6px 0 0' }}>
              {incident.verdict}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {incident.phase === 'COMMIT' && !hasCommitted && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <label style={{ fontSize: 12, color: '#9ca3af', display: 'block' }}>
                  Select Confidential Ballot:
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <button
                    type="button"
                    onClick={() => setSelectedChoice('GUILTY')}
                    style={{
                      padding: 12,
                      borderRadius: 12,
                      fontWeight: 700,
                      border: '1px solid',
                      cursor: 'pointer',
                      background:
                        selectedChoice === 'GUILTY'
                          ? 'rgba(225, 29, 72, 0.25)'
                          : 'rgba(255,255,255,0.05)',
                      borderColor:
                        selectedChoice === 'GUILTY' ? '#f43f5e' : 'rgba(255,255,255,0.1)',
                      color: selectedChoice === 'GUILTY' ? '#fda4af' : '#9ca3af',
                    }}
                  >
                    CONVICT (Guilty)
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedChoice('ACQUITTED')}
                    style={{
                      padding: 12,
                      borderRadius: 12,
                      fontWeight: 700,
                      border: '1px solid',
                      cursor: 'pointer',
                      background:
                        selectedChoice === 'ACQUITTED'
                          ? 'rgba(16, 185, 129, 0.25)'
                          : 'rgba(255,255,255,0.05)',
                      borderColor:
                        selectedChoice === 'ACQUITTED' ? '#10b981' : 'rgba(255,255,255,0.1)',
                      color: selectedChoice === 'ACQUITTED' ? '#6ee7b7' : '#9ca3af',
                    }}
                  >
                    DISMISS (Acquit)
                  </button>
                </div>
                <button
                  type="button"
                  onClick={handleCommit}
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: 12,
                    background: '#4f46e5',
                    border: 'none',
                    borderRadius: 12,
                    color: '#ffffff',
                    fontWeight: 700,
                    cursor: 'pointer',
                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.3)',
                  }}
                >
                  Encrypt & Submit Commit Hash
                </button>
              </div>
            )}

            {incident.phase === 'COMMIT' && hasCommitted && (
              <div
                style={{
                  textAlign: 'center',
                  padding: 16,
                  fontSize: 12,
                  color: '#a5b4fc',
                  background: 'rgba(99, 102, 241, 0.1)',
                  border: '1px solid rgba(99, 102, 241, 0.2)',
                  borderRadius: 12,
                }}
              >
                ✓ Your commit hash is recorded on ledger. Awaiting reveal phase.
              </div>
            )}

            {incident.phase === 'REVEAL' && !hasRevealed && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p style={{ fontSize: 12, color: '#fcd34d', margin: 0 }}>
                  Reveal phase is active. Broadcast your secret salt to verify your commitment.
                </p>
                <button
                  type="button"
                  onClick={handleReveal}
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: 12,
                    background: '#d97706',
                    border: 'none',
                    borderRadius: 12,
                    color: '#ffffff',
                    fontWeight: 700,
                    cursor: 'pointer',
                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.3)',
                  }}
                >
                  Verify Preimage & Reveal Vote
                </button>
              </div>
            )}

            {hasRevealed && (
              <div
                style={{
                  textAlign: 'center',
                  padding: 16,
                  fontSize: 12,
                  color: '#6ee7b7',
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  borderRadius: 12,
                }}
              >
                ✓ Preimage verified. Your vote is active in the quorum pool.
              </div>
            )}
          </div>
        )}

        {statusMsg && (
          <p
            style={{
              fontSize: 12,
              textAlign: 'center',
              marginTop: 16,
              color: '#9ca3af',
              fontFamily: 'monospace',
            }}
          >
            {statusMsg}
          </p>
        )}
      </div>
    </div>
  );
};
